import React, { useEffect, useState, useCallback } from "react";
import { Plus, Edit, Trash2, Search, ArrowUpDown } from "lucide-react";
import { toast } from "react-toastify";
import api from "../utils/api";
import Pagination from "../components/PaginationComp";

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 10,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("all");

  const [formData, setFormData] = useState({
    code: "",
    discount_value: "",
    discount_type: "percentage",
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: "",
    usage_limit: "",
    is_active: true
  });

  /* ================= FETCH COUPONS ================= */
  const fetchCoupons = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`admin/coupons`, {
        params: {
          page: currentPage,
          limit: pagination.limit,
          search: searchTerm || undefined,
          sort: sortOrder,
          sortBy: "createdAt",
          status: statusFilter !== "all" ? statusFilter : undefined,
        }
      });
      
      setCoupons(res.data.data || []);
      setPagination(res.data.pagination || {
        page: currentPage,
        totalPages: 1,
        totalItems: 0,
        limit: pagination.limit,
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch coupons");
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, sortOrder, statusFilter, pagination.limit]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  /* ================= CREATE / UPDATE ================= */
  const saveCoupon = async (e) => {
    e.preventDefault();

    if (formData.discount_type === "percentage" && 
        Number(formData.discount_value) > 100) {
      toast.error("Percentage discount cannot exceed 100%");
      return;
    }

    // Validate dates
    if (new Date(formData.valid_until) <= new Date(formData.valid_from)) {
      toast.error("Expiry date must be after start date");
      return;
    }

    // Prepare data with proper handling
    const dataToSend = {
      ...formData,
      valid_from: formData.valid_from || new Date().toISOString().split('T')[0],
      usage_limit: formData.usage_limit === "" ? null : Number(formData.usage_limit)
    };

    try {
      if (editingCoupon) {
        await api.patch(`coupons/${editingCoupon._id}`, dataToSend);
        toast.success("Coupon updated successfully!");
      } else {
        await api.post(`coupons`, dataToSend);
        toast.success("Coupon created successfully!");
      }

      resetForm();
      fetchCoupons();
    } catch (err) {
      console.error(err.response?.data || err);
      toast.error(err.response?.data?.message || "Failed to save coupon");
    }
  };

  /* ================= DEACTIVATE ================= */
  const deactivateCoupon = async (id, code) => {
    const toastId = toast.info(
      <div>
        <div className="mb-4">
          Are you sure you want to deactivate coupon <strong>{code}</strong>?
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={() => {
              toast.dismiss(toastId);
            }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              try {
                await api.patch(`coupons/${id}/deactivate`, { is_active: false });
                toast.dismiss(toastId);
                toast.success("Coupon deactivated successfully!");
                fetchCoupons();
              } catch (error) {
                console.error("Coupon deactivate error:", error);
                toast.error(error.response?.data?.message || "Failed to deactivate coupon!");
              }
            }}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700"
          >
            Yes, Deactivate
          </button>
        </div>
      </div>,
      // {
      //   position: "top-center",
      //   autoClose: false,
      //   closeOnClick: false,
      //   draggable: false,
      //   closeButton: true,
      // }
    );
  };

  /* ================= HELPERS ================= */
  const resetForm = () => {
    setEditingCoupon(null);
    setShowModal(false);
    setFormData({
      code: "",
      discount_value: "",
      discount_type: "percentage",
      valid_from: new Date().toISOString().split('T')[0],
      valid_until: "",
      usage_limit: "",
      is_active: true
    });
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const getStatusColor = (isActive) => {
    return isActive 
      ? "bg-emerald-100 text-emerald-800 border border-emerald-200" 
      : "bg-red-100 text-red-800 border border-red-200";
  };

  const getDaysRemaining = (date) => {
    if (!date) return null;
    const today = new Date();
    const expiry = new Date(date);
    const diffTime = expiry - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Check if coupon is active based on dates
  const isCouponActive = (coupon) => {
    if (!coupon.is_active) return false;
    
    const now = new Date();
    const validFrom = coupon.valid_from ? new Date(coupon.valid_from) : null;
    const validTill = coupon.valid_until ? new Date(coupon.valid_until) : null;
    
    if (validFrom && now < validFrom) return false;
    if (validTill && now > validTill) return false;
    
    return true;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSortChange = () => {
    setSortOrder(sortOrder === "desc" ? "asc" : "desc");
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Coupon Management</h2>
            <p className="text-gray-600">Manage discount coupons</p>
          </div>

          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2.5 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
          >
            <Plus size={18} />
            Create Coupon
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search coupon code..."
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Status Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => handleStatusFilter("all")}
              className={`px-3 py-1.5 rounded-lg text-sm ${
                statusFilter === "all"
                  ? "bg-gray-800 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All
            </button>
            <button
              onClick={() => handleStatusFilter("active")}
              className={`px-3 py-1.5 rounded-lg text-sm ${
                statusFilter === "active"
                  ? "bg-emerald-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Active
            </button>
            <button
              onClick={() => handleStatusFilter("inactive")}
              className={`px-3 py-1.5 rounded-lg text-sm ${
                statusFilter === "inactive"
                  ? "bg-red-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Inactive
            </button>
            <button
              onClick={() => handleStatusFilter("expired")}
              className={`px-3 py-1.5 rounded-lg text-sm ${
                statusFilter === "expired"
                  ? "bg-orange-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Expired
            </button>
          </div>

          {/* Sort Button */}
          <button
            onClick={handleSortChange}
            className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors flex items-center gap-2"
          >
            <ArrowUpDown className="w-4 h-4" />
            {sortOrder === "desc" ? "Newest" : "Oldest"}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                Coupon Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                Discount
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                Validity
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                Usage
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              // Loading Skeletons
              [...Array(5)].map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-28"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-6 bg-gray-200 rounded w-16"></div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="h-8 bg-gray-200 rounded w-20"></div>
                  </td>
                </tr>
              ))
            ) : coupons.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                  No coupons found
                </td>
              </tr>
            ) : (
              coupons.map((coupon) => {
                const daysRemaining = getDaysRemaining(coupon.valid_until);
                const isActive = isCouponActive(coupon);
                
                return (
                  <tr key={coupon._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <code className="font-mono font-bold text-gray-800">
                        {coupon.code}
                      </code>
                      {coupon.description && (
                        <p className="text-sm text-gray-500 mt-1">{coupon.description}</p>
                      )}
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <span className="font-bold text-gray-800">
                          {coupon.discount_type === "percentage" 
                            ? `${coupon.discount_value}%`
                            : `₹${coupon.discount_value}`}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {coupon.discount_type === "percentage" ? "Percentage" : "Fixed"}
                      </p>
                    </td>

                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          From: {formatDate(coupon.valid_from)}
                        </p>
                        <p className="text-sm font-medium text-gray-800">
                          Till: {formatDate(coupon.valid_until)}
                        </p>
                        {daysRemaining !== null && (
                          <p className={`text-sm font-medium mt-1 ${
                            daysRemaining > 30 ? "text-emerald-600" :
                            daysRemaining > 0 ? "text-amber-600" :
                            "text-red-600"
                          }`}>
                            {daysRemaining >= 0 
                              ? `${daysRemaining} days left`
                              : "Expired"}
                          </p>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Used:</span>
                          <span className="font-medium">{coupon.used_count || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Limit:</span>
                          <span className="font-medium">
                            {coupon.usage_limit || 100}
                          </span>
                        </div>
                        {coupon.usage_limit && (
                          <div className="h-1 bg-gray-200 rounded-full overflow-hidden mt-1">
                            <div 
                              className="h-full bg-blue-500"
                              style={{ 
                                width: `${Math.min(((coupon.used_count || 0) / coupon.usage_limit) * 100, 100)}%` 
                              }}
                            ></div>
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(isActive)}`}>
                          {isActive ? "Active" : "Inactive"}
                        </span>
                        {coupon.valid_until && new Date(coupon.valid_until) < new Date() && (
                          <p className="text-xs text-red-600">Expired</p>
                        )}
                        {coupon.valid_from && new Date(coupon.valid_from) > new Date() && (
                          <p className="text-xs text-amber-600">Not started</p>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingCoupon(coupon);
                            setFormData({
                              code: coupon.code,
                              discount_value: coupon.discount_value,
                              discount_type: coupon.discount_type,
                              valid_from: coupon.valid_from?.split("T")[0] || new Date().toISOString().split('T')[0],
                              valid_until: coupon.valid_until?.split("T")[0] || "",
                              usage_limit: coupon.usage_limit || "",
                              is_active: coupon.is_active
                            });
                            setShowModal(true);
                          }}
                          className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
                          title="Edit Coupon"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => deactivateCoupon(coupon._id, coupon.code)}
                          className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
                          title="Deactivate Coupon"
                          disabled={!coupon.is_active}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="p-6 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-600">
              Page {currentPage} of {pagination.totalPages} • 
              Showing {coupons.length} of {pagination.totalRecords} coupons
              {statusFilter !== "all" && ` • Filtered: ${statusFilter}`}
            </p>
            <Pagination
              totalPages={pagination.totalPages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800">
                {editingCoupon ? "Edit Coupon" : "Create New Coupon"}
              </h3>
            </div>

            <form onSubmit={saveCoupon} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coupon Code *
                </label>
                <input
                  placeholder="e.g., SUMMER25"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      code: e.target.value.toUpperCase().replace(/\s+/g, '')
                    })
                  }
                  required
                  disabled={!!editingCoupon}
                  className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Type *
                  </label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        discount_type: e.target.value
                      })
                    }
                    className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.discount_type === "percentage" ? "Discount % *" : "Amount (₹) *"}
                  </label>
                  <input
                    type="number"
                    placeholder={formData.discount_type === "percentage" ? "25" : "500"}
                    value={formData.discount_value}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        discount_value: e.target.value
                      })
                    }
                    required
                    min="1"
                    max={formData.discount_type === "percentage" ? "100" : undefined}
                    className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        valid_from: e.target.value
                      })
                    }
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expiry Date *
                  </label>
                  <input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        valid_until: e.target.value
                      })
                    }
                    required
                    min={formData.valid_from || new Date().toISOString().split('T')[0]}
                    className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Usage Limit
                  <span className="text-gray-500 text-sm ml-1">(leave empty for 100)</span>
                </label>
                <input
                  type="number"
                  placeholder="100"
                  value={formData.usage_limit}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      usage_limit: e.target.value
                    })
                  }
                  min="1"
                  className="w-full border border-gray-300 px-4 py-2.5 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Number of times this coupon can be used. Empty means 100 usage.
                </p>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      is_active: e.target.checked
                    })
                  }
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                  Active Coupon
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingCoupon ? "Update Coupon" : "Create Coupon"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponManagement;